import './App.css';
import React, {createContext, useEffect, useState} from "react";
import ReactGA from "react-ga4";
import {Context} from "react";
import {
    SDKProvider,
    bindViewportCSSVars,
    postEvent,
    useViewport,
    useMiniApp,
    bindMiniAppCSSVars, useThemeParams, useInitData
} from "@tma.js/sdk-react";
import {TonConnectUIProvider} from "@tonconnect/ui-react";
import {frontendAddress} from "./communication/communication";
import {Notifications} from "./components/Common/Notifications/Notifications";
import {BackButtonProvider} from "./components/Common/Elements/BackButtonProvider";
import {useTranslation} from "react-i18next";

/* Needed everywhere */
import './components/Common/Elements/Text.css'
import {Popups} from "./components/Common/Elements/Popups";
import {Navigation, NavigationProps} from "./components/Common/Navigation/Navigation";
import {UserProfileView} from "./components/UserProfile/UserProfile";
import {WalletViewDelayed} from "./components/Wallet/WalletView";
import {TasksView} from "./components/TasksView/TasksView";
import {NavigationFooter} from "./components/Common/Navigation/NavigationFooter";
import {AuthLayer} from "./components/Common/Authorization/AuthLayer";
import {ExplytStore} from "./components/ExplytStore/ExplytStore";
import {UserProvider} from "./components/Common/Authorization/UserProvider";

// Analytics
if (process.env.NODE_ENV === "production") {
    ReactGA.initialize("G-7NZPL0DXYN");
}

export let NotificationsContext : Context<(text: string) => void>;

function App() {
    return (
        <div style={{width: "100%", height: "100%", position: "relative"}}>
            <TonConnectUIProvider manifestUrl={`${frontendAddress}${process.env.PUBLIC_URL}/tonconnect-manifest.json`}>
                <SDKProvider>
                    <BackButtonProvider>
                        <MiniApp />
                    </BackButtonProvider>
                </SDKProvider>
            </TonConnectUIProvider>
        </div>
    );
}

function trySetFullscreen() {
    try {
        if ((window as any).Telegram.WebApp){
            (window as any).Telegram.WebApp.ready();
            (window as any).Telegram.WebApp.expand();
        } else {
        }
    }
    catch (err) {
        console.error(err);
    }
}

function MainContent(){
    const navigationProps : NavigationProps = {
        isMain: true,
        screens: [<UserProfileView/>, <ExplytStore/>, <WalletViewDelayed/>, <TasksView/>],
        navigation: navProps => <NavigationFooter props={navProps}/>,
        orientation: "down",
        useGestures: false,
        navigationStackHeight: "var(--app-height)"
    }

    return <Navigation {...navigationProps}/>
}

function MiniApp() {
    const [notifications, setNotifications] = useState([] as string[]);
    const viewport = useViewport(true);
    const app = useMiniApp();
    const themeParams = useThemeParams();
    const {i18n} = useTranslation();
    const initData = useInitData()

    useEffect(() => {
        if (initData?.user) {
            const allowedLanguages = ["ru", "en"]
            const requiredLanguage = initData.user.languageCode ?? "en"
            if (allowedLanguages.includes(requiredLanguage)) i18n.changeLanguage(requiredLanguage)
        }
    }, [i18n, initData?.user]);

    const addNotification = (text : string) => {
        if (window.navigator.vibrate)
            window.navigator.vibrate(50);
        setNotifications(notifications.concat([text]));
    }
    
    NotificationsContext = createContext(addNotification)
    
    useEffect(() => {
        setTimeout(trySetFullscreen, 1000);
        if (viewport === undefined) return;
        postEvent('web_app_expand');
        viewport?.expand()
        bindViewportCSSVars(viewport);
    }, [viewport]);

    useEffect(() => {
        if (app === undefined || themeParams === undefined) return;
        app.setHeaderColor('#000000');
        app.setBgColor('#000000');
        bindMiniAppCSSVars(app, themeParams);
    }, [app, themeParams]);

    return (
        <div className="navigation-main">
            <Notifications notificationList={notifications}/>
            <AuthLayer>
                <UserProvider>
                    <MainContent/>
                </UserProvider>
            </AuthLayer>
            <Popups/>
        </div>
    );
}

export default App;
